import { useMemo, useState } from "react";
import { Layout } from "@/components/layout";
import { displayQty } from "@/components/inventory-cells";
import {
  useGetAccount,
  useUpdateAccount,
  useCreateVirtualBankAccount,
  useUpdateVirtualBankAccount,
  useTestAccountPhone,
  useListAccountPhoneTests,
  useListAuditLog,
  useGetAccountBankHistory,
  getGetAccountBankHistoryQueryKey,
  getListAuditLogQueryOptions,
  getGetAccountQueryKey,
  getListAccountPhoneTestsQueryKey,
  type VirtualBankAccount,
  type AccountDetail as AccountDetailModel,
  type MicroDeposit,
  type AccountPhoneStatus,
  type AuditLogEntry,
  type BankSwapHistoryEntry,
} from "@workspace/api-client-react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { ActivityTable } from "@/components/activity-feed";
import { Loader2, ArrowLeft, Plus, Pencil, Send, ArrowRight, Flame } from "lucide-react";
import { useRoute, Link } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PhoneTestsTable } from "@/components/phone-tests-table";

function TestPhoneButton({
  accountId,
  hasPhone,
  onTested,
}: {
  accountId: number;
  hasPhone: boolean;
  onTested: () => void;
}) {
  const test = useTestAccountPhone({
    mutation: {
      onSuccess: (res) => {
        if (res.status === "sent") toast.success(`Test SMS sent to ${res.phoneNumber}`);
        else if (res.status === "skipped") toast.warning(res.error || "No phone on file");
        else toast.error(res.error || "Test SMS failed");
        onTested();
      },
      onError: () => toast.error("Twilio is not configured"),
    },
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2 text-xs rounded-full"
      disabled={test.isPending || !hasPhone}
      onClick={() => test.mutate({ id: accountId })}
    >
      {test.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Send className="h-3 w-3 text-blue-400" />
      )}
      <span className="ml-1">Test SMS</span>
    </Button>
  );
}

export default function AccountDetail() {
  const [, params] = useRoute("/accounts/:id");
  const accountId = params?.id ? parseInt(params.id) : 0;
  const qc = useQueryClient();

  const { data: account, isLoading: accountLoading } = useGetAccount(accountId, {
    query: { enabled: !!accountId, queryKey: getGetAccountQueryKey(accountId) },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getGetAccountQueryKey(accountId) });
    qc.invalidateQueries({ queryKey: getListAccountPhoneTestsQueryKey(accountId) });
  };

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background overflow-hidden">
        <div className="p-4 border-b border-border bg-card shrink-0">
          <Link href="/accounts" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="h-3 w-3 mr-1" />
            Back to Accounts
          </Link>
          {accountLoading ? (
            <div className="flex items-center h-8">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            </div>
          ) : account ? (
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-xl font-medium text-foreground flex items-center gap-2">
                  {account.accountName || account.vendorAccountEmail}
                  {account.ampMatch && <Badge variant="secondary" className="text-[10px] uppercase font-mono">AMP Linked</Badge>}
                </h1>
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-4">
                  <span>{account.vendorAccountEmail}</span>
                  {account.pullerName && (
                    <span className="flex items-center gap-1">
                      <span className="text-xs text-secondary-foreground">Puller:</span> {account.pullerName}
                    </span>
                  )}
                </div>
              </div>
              <EditAccountDialog accountId={accountId} account={account} onSaved={invalidate} />
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-auto custom-scrollbar p-4">
          {account && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="bg-muted w-full justify-start rounded-md border border-border h-9 p-1">
                <TabsTrigger value="overview" className="text-xs h-7">Overview</TabsTrigger>
                <TabsTrigger value="inventory" className="text-xs h-7">Inventory</TabsTrigger>
                <TabsTrigger value="banking" className="text-xs h-7">Virtual Banking</TabsTrigger>
                <TabsTrigger value="juice" className="text-xs h-7">Juice Rentals</TabsTrigger>
                <TabsTrigger value="activity" className="text-xs h-7">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-border bg-card p-4 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone Details</h3>
                      <TestPhoneButton accountId={accountId} hasPhone={!!account.wrPhone} onTested={invalidate} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <Row label="Phone on File" value={account.wrPhone || "None"} mono />
                      <div className="flex justify-between py-1 border-b border-border/50">
                        <span className="text-muted-foreground">Status</span>
                        <Badge
                          variant="outline"
                          className={`uppercase font-mono text-[10px] ${
                            account.phoneStatus === "good"
                              ? "border-green-500/30 text-green-500 bg-green-500/10"
                              : account.phoneStatus === "bad"
                                ? "border-red-500/30 text-red-500 bg-red-500/10"
                                : ""
                          }`}
                        >
                          {account.phoneStatus || "UNKNOWN"}
                          {account.phoneStatusManual ? " ·M" : ""}
                          {account.phoneStatus === "good" || account.phoneStatus === "bad" ? (
                            <span
                              className="ml-1 text-[8px] text-muted-foreground/60"
                              title="Synced to Reach"
                            >
                              ↑R
                            </span>
                          ) : null}
                        </Badge>
                      </div>
                      <Row label="Phone Type" value={account.wrPhoneType || "—"} />
                      <Row
                        label="Last SMS (AMP)"
                        value={account.lastSmsTime ? new Date(account.lastSmsTime).toLocaleString() : "Never"}
                      />
                      <Row label="SMS Note" value={account.lastSms || "—"} />
                    </div>
                  </div>

                  <div className="border border-border bg-card p-4 rounded-md">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Account Security</h3>
                    <div className="space-y-2 text-sm">
                      <Row label="TM Password" value={account.tmPassword ? "••••••••" : "Not set"} mono />
                      <Row label="Label" value={account.label || "—"} />
                      <Row label="Bank Account" value={account.bankCardName || account.bankName || "Not assigned"} />
                      {account.bankMappingStatus && (
                        <>
                          <Row
                            label="Routing #"
                            value={account.bankRouting || (account.bankRoutingLast4 ? `••${account.bankRoutingLast4}` : "—")}
                            mono
                          />
                          <Row
                            label="Account #"
                            value={account.bankAccount || (account.bankAccountLast4 ? `••${account.bankAccountLast4}` : "—")}
                            mono
                          />
                        </>
                      )}
                      <Row label="Active Inventory" value={String(account.activeInventoryCount)} mono />
                    </div>
                  </div>
                </div>

                <div className="border border-border bg-card p-4 rounded-md">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Phone Test History
                  </h3>
                  <PhoneTestHistory accountId={accountId} />
                </div>
              </TabsContent>

              <TabsContent value="inventory" className="mt-4">
                <div className="border border-border rounded overflow-x-auto custom-scrollbar">
                  <table className="w-full text-xs min-w-[480px]">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium">Section</th>
                        <th className="text-left py-2 px-3 font-medium">Row</th>
                        <th className="text-right py-2 px-3 font-medium">Qty</th>
                        <th className="text-left py-2 px-3 font-medium">Hold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(account.inventory ?? []).map((i) => (
                        <tr key={i.id} className="border-t border-border">
                          <td className="py-1.5 px-3">{i.section}</td>
                          <td className="py-1.5 px-3">{i.row}</td>
                          <td className="py-1.5 px-3 text-right font-mono">{displayQty(i)}</td>
                          <td className="py-1.5 px-3">{i.adminHoldActive ? "Held" : "—"}</td>
                        </tr>
                      ))}
                      {(!account.inventory || account.inventory.length === 0) && (
                        <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No active inventory.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="banking" className="mt-4">
                <BankingTab
                  accountId={accountId}
                  email={account.vendorAccountEmail}
                  vbas={account.virtualBankAccounts ?? []}
                  microDeposits={account.microDeposits ?? []}
                  onChanged={invalidate}
                />
              </TabsContent>

              <TabsContent value="juice" className="mt-4">
                <div className="border border-border rounded overflow-x-auto custom-scrollbar">
                  <table className="w-full text-xs min-w-[480px]">
                    <thead className="bg-muted text-muted-foreground">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium">Renter</th>
                        <th className="text-left py-2 px-3 font-medium">Tag</th>
                        <th className="text-left py-2 px-3 font-medium">Period</th>
                        <th className="text-left py-2 px-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(account.juiceRentals ?? []).map((j) => (
                        <tr key={j.id} className="border-t border-border">
                          <td className="py-1.5 px-3">{j.renterName || "—"}</td>
                          <td className="py-1.5 px-3 font-mono">{j.renterPullerTag || "—"}</td>
                          <td className="py-1.5 px-3">{[j.rentalStart, j.rentalEnd].filter(Boolean).join(" → ") || "—"}</td>
                          <td className="py-1.5 px-3">{j.status || "—"}</td>
                        </tr>
                      ))}
                      {(!account.juiceRentals || account.juiceRentals.length === 0) && (
                        <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No juice rental history.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="activity" className="mt-4">
                <AccountActivityTab
                  accountId={accountId}
                  inventoryIds={(account.inventory ?? []).map((i) => i.id)}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </Layout>
  );
}

function AccountActivityTab({
  accountId,
  inventoryIds,
}: {
  accountId: number;
  inventoryIds: number[];
}) {
  const { data: accountData, isLoading } = useListAuditLog({
    entityType: "account",
    entityId: String(accountId),
    limit: 100,
  });

  // Listing-level events are keyed by inventory row id, so pull each active
  // listing's audit trail and merge it into the account timeline.
  const invQueries = useQueries({
    queries: inventoryIds.map((id) =>
      getListAuditLogQueryOptions({
        entityType: "inventory",
        entityId: String(id),
        limit: 50,
      }),
    ),
  });
  const invLoading = invQueries.some((q) => q.isLoading);

  const merged = useMemo(() => {
    const map = new Map<number, AuditLogEntry>();
    for (const e of accountData?.items ?? []) map.set(e.id, e);
    for (const q of invQueries) {
      for (const e of q.data?.items ?? []) map.set(e.id, e);
    }
    return [...map.values()].sort((a, b) =>
      (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
    );
  }, [accountData, invQueries]);

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Link href={`/activity?entityType=account&entityId=${accountId}`}>
          <span className="text-xs text-primary hover:underline cursor-pointer">
            View in full Activity →
          </span>
        </Link>
      </div>
      <ActivityTable
        entries={merged}
        isLoading={isLoading || invLoading}
        showEntity={false}
        emptyText="No activity recorded for this account yet."
      />
    </div>
  );
}

function PhoneTestHistory({ accountId }: { accountId: number }) {
  const { data: tests, isLoading } = useListAccountPhoneTests(accountId, {
    query: {
      enabled: !!accountId,
      queryKey: getListAccountPhoneTestsQueryKey(accountId),
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      </div>
    );
  }

  return <PhoneTestsTable tests={tests ?? []} />;
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1 border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value}</span>
    </div>
  );
}

function EditAccountDialog({
  accountId,
  account,
  onSaved,
}: {
  accountId: number;
  account: AccountDetailModel;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const update = useUpdateAccount({
    mutation: {
      onSuccess: () => { onSaved(); setOpen(false); toast.success("Account updated"); },
      onError: () => toast.error("Failed to update account"),
    },
  });
  const [form, setForm] = useState({
    accountName: account.accountName ?? "",
    label: account.label ?? "",
    tmPassword: "",
    wrPhone: account.wrPhone ?? "",
    wrPhoneType: account.wrPhoneType ?? "",
    phoneStatus: account.phoneStatus ?? "unknown",
    lastSms: account.lastSms ?? "",
  });

  const save = () => {
    update.mutate({
      id: accountId,
      data: {
        accountName: form.accountName,
        label: form.label,
        ...(form.tmPassword ? { tmPassword: form.tmPassword } : {}),
        wrPhone: form.wrPhone,
        wrPhoneType: form.wrPhoneType,
        ...(form.phoneStatus !== (account.phoneStatus ?? "unknown")
          ? { phoneStatus: form.phoneStatus as AccountPhoneStatus }
          : {}),
        lastSms: form.lastSms,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => setOpen(true)}>
        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
      </Button>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Account</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Account Name"><Input className="h-8 text-xs" value={form.accountName} onChange={(e) => setForm({ ...form, accountName: e.target.value })} /></Field>
          <Field label="Label"><Input className="h-8 text-xs" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></Field>
          <Field label="WR Phone"><Input className="h-8 text-xs" value={form.wrPhone} onChange={(e) => setForm({ ...form, wrPhone: e.target.value })} /></Field>
          <Field label="Phone Type"><Input className="h-8 text-xs" value={form.wrPhoneType} onChange={(e) => setForm({ ...form, wrPhoneType: e.target.value })} /></Field>
          <Field label="Phone Status">
            <Select value={form.phoneStatus} onValueChange={(v) => setForm({ ...form, phoneStatus: v as AccountPhoneStatus })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="bad">Bad</SelectItem>
                <SelectItem value="unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Last SMS"><Input className="h-8 text-xs" value={form.lastSms} onChange={(e) => setForm({ ...form, lastSms: e.target.value })} /></Field>
          <Field label="TM Password (set new)"><Input type="password" className="h-8 text-xs" value={form.tmPassword} onChange={(e) => setForm({ ...form, tmPassword: e.target.value })} placeholder="Leave blank to keep" /></Field>
        </div>
        <DialogFooter>
          <Button size="sm" onClick={save} disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function bankLabel(
  cardName: string | null | undefined,
  key: string | null | undefined,
  variant: string | null | undefined,
): string {
  const name = cardName || key || "—";
  return variant ? `${name} · ${variant}` : name;
}

function BankSwapHistory({ email }: { email: string }) {
  const { data, isLoading } = useGetAccountBankHistory(
    { email },
    {
      query: {
        enabled: !!email,
        queryKey: getGetAccountBankHistoryQueryKey({ email }),
      },
    },
  );

  const entries: BankSwapHistoryEntry[] = data ?? [];

  return (
    <div className="border border-border bg-card p-4 rounded-md">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Bank Assignment History
      </h3>
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          No bank assignment changes recorded for this account yet.
        </p>
      ) : (
        <ol className="space-y-3">
          {entries.map((e) => (
            <li key={e.id} className="flex items-start gap-3 text-xs">
              <span
                className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                  e.action === "burn"
                    ? "bg-red-500/10 text-red-500"
                    : e.viaBurn
                      ? "bg-amber-500/10 text-amber-500"
                      : "bg-green-500/10 text-green-500"
                }`}
              >
                {e.action === "burn" ? (
                  <Flame className="h-3 w-3" />
                ) : (
                  <ArrowRight className="h-3 w-3" />
                )}
              </span>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {e.action === "burn" ? (
                    <span className="font-medium text-foreground">
                      Burned off {bankLabel(e.cardName, e.bankAccountKey, e.variant)}
                    </span>
                  ) : e.action === "unassign" ? (
                    <span className="font-medium text-foreground">
                      Removed from {bankLabel(e.cardName, e.bankAccountKey, e.variant)}
                    </span>
                  ) : e.viaBurn ? (
                    <span className="flex flex-wrap items-center gap-1.5 font-medium text-foreground">
                      Re-homed
                      <span className="font-mono text-muted-foreground">
                        {bankLabel(e.fromCardName, e.fromBankAccountKey, e.fromVariant)}
                      </span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      <span className="font-mono">
                        {bankLabel(e.cardName, e.bankAccountKey, e.variant)}
                      </span>
                      <Badge variant="outline" className="text-[9px] uppercase border-amber-500/30 text-amber-500">
                        via burn
                      </Badge>
                    </span>
                  ) : (
                    <span className="font-medium text-foreground">
                      Assigned to {bankLabel(e.cardName, e.bankAccountKey, e.variant)}
                    </span>
                  )}
                  {e.accountNumberLast4 && (
                    <span className="font-mono text-muted-foreground">
                      (••{e.accountNumberLast4})
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {e.createdAt ? new Date(e.createdAt).toLocaleString() : "—"}
                  {e.actorEmail ? ` · ${e.actorEmail}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function BankingTab({
  accountId,
  email,
  vbas,
  microDeposits,
  onChanged,
}: {
  accountId: number;
  email: string;
  vbas: VirtualBankAccount[];
  microDeposits: MicroDeposit[];
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<VirtualBankAccount | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="space-y-5">
      <BankSwapHistory email={email} />
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Virtual Checking Accounts</h3>
        <Button size="sm" className="h-8" onClick={() => setAdding(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Virtual Account
        </Button>
      </div>

      <div className="border border-border rounded overflow-x-auto custom-scrollbar">
        <table className="w-full text-xs min-w-[480px]">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium">Bank</th>
              <th className="text-left py-2 px-3 font-medium">Routing</th>
              <th className="text-left py-2 px-3 font-medium">Account #</th>
              <th className="text-left py-2 px-3 font-medium">Mercury</th>
              <th className="text-left py-2 px-3 font-medium">Status</th>
              <th className="text-left py-2 px-3 font-medium">Sweep</th>
              <th className="py-2 px-3" />
            </tr>
          </thead>
          <tbody>
            {vbas.map((v) => (
              <tr key={v.id} className="border-t border-border hover:bg-muted/40">
                <td className="py-1.5 px-3">{v.bankName || "—"}</td>
                <td className="py-1.5 px-3 font-mono">{v.routingLast4 ? `••${v.routingLast4}` : "—"}</td>
                <td className="py-1.5 px-3 font-mono">{v.accountNumberLast4 ? `••${v.accountNumberLast4}` : "—"}</td>
                <td className="py-1.5 px-3">{v.mercuryNickname || v.mercuryAccountId || "—"}</td>
                <td className="py-1.5 px-3">{v.status || "—"}</td>
                <td className="py-1.5 px-3">{v.sweepRuleStatus || v.sweepRuleDestinationLabel || "—"}</td>
                <td className="py-1.5 px-3 text-right">
                  <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setEditing(v)}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
            {vbas.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">No virtual checking accounts.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Micro-Deposits</h3>
        <div className="border border-border rounded overflow-x-auto custom-scrollbar">
          <table className="w-full text-xs min-w-[480px]">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Posted</th>
                <th className="text-right py-2 px-3 font-medium">Amount</th>
                <th className="text-left py-2 px-3 font-medium">Description</th>
                <th className="text-left py-2 px-3 font-medium">Match</th>
                <th className="text-left py-2 px-3 font-medium">Slack</th>
              </tr>
            </thead>
            <tbody>
              {(microDeposits ?? []).map((m) => (
                <tr key={m.id} className="border-t border-border">
                  <td className="py-1.5 px-3">{m.postedAt ? new Date(m.postedAt).toLocaleString() : "—"}</td>
                  <td className="py-1.5 px-3 text-right font-mono">{m.amount != null ? `${m.amount} ${m.currency || ""}` : "—"}</td>
                  <td className="py-1.5 px-3">{m.description || "—"}</td>
                  <td className="py-1.5 px-3">{m.matchStatus || "—"}</td>
                  <td className="py-1.5 px-3">{m.slackAlertStatus || "—"}</td>
                </tr>
              ))}
              {(!microDeposits || microDeposits.length === 0) && (
                <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No micro-deposits recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {adding && (
        <VbaDialog
          accountId={accountId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChanged(); }}
        />
      )}
      {editing && (
        <VbaDialog
          accountId={accountId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function VbaDialog({
  accountId,
  existing,
  onClose,
  onSaved,
}: {
  accountId: number;
  existing?: VirtualBankAccount;
  onClose: () => void;
  onSaved: () => void;
}) {
  const create = useCreateVirtualBankAccount({
    mutation: { onSuccess: () => { onSaved(); toast.success("Virtual account added"); }, onError: () => toast.error("Failed to add account") },
  });
  const update = useUpdateVirtualBankAccount({
    mutation: { onSuccess: () => { onSaved(); toast.success("Virtual account updated"); }, onError: () => toast.error("Failed to update account") },
  });
  const [form, setForm] = useState({
    bankName: existing?.bankName ?? "",
    routingNumber: "",
    accountNumber: "",
    mercuryAccountId: existing?.mercuryAccountId ?? "",
    mercuryNickname: existing?.mercuryNickname ?? "",
    country: existing?.country ?? "",
    status: existing?.status ?? "",
    sweepRuleDestinationLabel: existing?.sweepRuleDestinationLabel ?? "",
    notes: existing?.notes ?? "",
  });
  const pending = create.isPending || update.isPending;

  const save = () => {
    if (!form.bankName.trim()) { toast.error("Bank name is required"); return; }
    const data = {
      bankName: form.bankName.trim(),
      ...(form.routingNumber ? { routingNumber: form.routingNumber } : {}),
      ...(form.accountNumber ? { accountNumber: form.accountNumber } : {}),
      mercuryAccountId: form.mercuryAccountId,
      mercuryNickname: form.mercuryNickname,
      country: form.country,
      status: form.status,
      sweepRuleDestinationLabel: form.sweepRuleDestinationLabel,
      notes: form.notes,
    };
    if (existing) update.mutate({ id: existing.id, data });
    else create.mutate({ id: accountId, data });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{existing ? "Edit Virtual Account" : "Add Virtual Account"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Bank Name *"><Input className="h-8 text-xs" value={form.bankName} onChange={(e) => setForm({ ...form, bankName: e.target.value })} /></Field>
          <Field label="Status"><Input className="h-8 text-xs" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} /></Field>
          <Field label={existing ? "Routing # (set new)" : "Routing #"}><Input className="h-8 text-xs" value={form.routingNumber} onChange={(e) => setForm({ ...form, routingNumber: e.target.value })} placeholder={existing?.routingLast4 ? `••${existing.routingLast4}` : ""} /></Field>
          <Field label={existing ? "Account # (set new)" : "Account #"}><Input className="h-8 text-xs" value={form.accountNumber} onChange={(e) => setForm({ ...form, accountNumber: e.target.value })} placeholder={existing?.accountNumberLast4 ? `••${existing.accountNumberLast4}` : ""} /></Field>
          <Field label="Mercury Account ID"><Input className="h-8 text-xs" value={form.mercuryAccountId} onChange={(e) => setForm({ ...form, mercuryAccountId: e.target.value })} /></Field>
          <Field label="Mercury Nickname"><Input className="h-8 text-xs" value={form.mercuryNickname} onChange={(e) => setForm({ ...form, mercuryNickname: e.target.value })} /></Field>
          <Field label="Country"><Input className="h-8 text-xs" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
          <Field label="Sweep Destination"><Input className="h-8 text-xs" value={form.sweepRuleDestinationLabel} onChange={(e) => setForm({ ...form, sweepRuleDestinationLabel: e.target.value })} /></Field>
          <div className="col-span-2">
            <Field label="Notes"><Input className="h-8 text-xs" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={pending}>
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
